
import Asset from '@components/tree/Asset.vue';
import Directory from '@components/tree/Directory.vue';
import DirectoryResource from '@structure/Resources/Directory';
import { resourceRouteDefinition, stateAwareUrl } from '@/support';

const sortByDimensions = (a, b) => {
  // sort by width
  const diff = Math.sign(a.width() - b.width());

  if (diff !== 0) {
    return diff;
  }

  // if width is the same, sort by height
  return Math.sign(a.height() - b.height());
};

export default {
  name: 'directory',

  data() {
    return {
      expandReactivityTrigger: Number(new Date()),
    };
  },

  props: {
    resource: {
      type: DirectoryResource,
      required: true,
    },
  },

  computed: {
    icon() {
      if (this.resource.gallery()) {
        return 'images';
      }

      return this.isExpanded ? 'folder-open' : 'folder';
    },

    classes() {
      const classes = [this.isExpanded ? 'expanded' : 'collapsed'];

      if (this.resource.gallery()) {
        classes.push('gallery');
      }

      return classes;
    },

    // filters & sorts the directories and assets for display in the sidebar
    children() {
      const children = [];

      // pluck the child directories; clone each as a new resource
      const directories = this.resource
        .children()
        .filter(c => c.isDirectory())
        .map(c => c.clone());

      // pluck version directories. mark previous versions as 'old', and
      // apply them in order to the result array we're building up
      directories
        .filter(c => c.isVersioned())
        .sort((a, b) => Math.sign(b.version() - a.version()))
        .forEach((c, idx) => {
          c.setMeta('obsolete', idx !== 0);
          children.unshift(c);
        });

      // now apply the other non-version directories to the result array
      directories.filter(c => !c.isVersioned()).forEach(c => children.push(c));

      // now apply regular file banners to the result array
      this.resource
        .children()
        .filter(c => c.isBanner()) // only deal with banners
        .sort(sortByDimensions)
        .sort((a, b) => {
          return Math.sign(Number(b.isEspnUap()) - Number(a.isEspnUap()));
        }) // put UAP banners up top
        .forEach(c => children.push(c));

      // now add non-banner, non-directory assets
      this.resource
        .children()
        .filter(c => !c.isBanner() && !c.isDirectory())
        .sort(sortByDimensions)
        .forEach(c => children.push(c));

      return children;
    },

    url() {
      return stateAwareUrl(this.resource);
    },

    toggleClass() {
      return this.isExpanded ? 'caret-down' : 'caret-right';
    },

    isExpanded() {
      const _ = this.expandReactivityTrigger; // this just properly sets up reactivity
      const sessionValue = sessionStorage.getItem(this.resource.cid());

      if (sessionValue !== null) {
        return Number(sessionValue) !== 0;
      }

      if (this.resource.isRoot()) {
        return true;
      }

      // collapse old version folders for a banner
      return !this.resource.getMeta('obsolete', false);
    },

    canCollapse() {
      return !this.resource.isRoot();
    },
  },

  methods: {
    typeToComponent(type) {
      return type === 'directory' ? 'directory' : 'asset';
    },

    toggle() {
      const newState = !this.isExpanded;

      sessionStorage.setItem(this.resource.cid(), String(Number(newState)));

      this.expandReactivityTrigger = Number(new Date());
    },

    checkAction($event) {
      if (this.isExpanded) {
        return;
      }

      $event.preventDefault();

      this.toggle();
    },
  },

  components: {
    Asset,
    Directory: () => import('./Directory.vue'),
  },
};
